import { MAX_CHALLENGES } from '../constants/settings'
import {
  GameStats,
  loadStatsFromLocalStorage,
  saveStatsToLocalStorage,
} from './localStorage'

// In stats array elements 0-5 are successes in 1-6 trys

export const addStatsForCompletedGame = (
  gameStats: GameStats,
  count: number,
  company?: string
) => {
  // Count is number of incorrect guesses before end.
  const stats = { ...gameStats }

  stats.totalGames += 1

  if (count >= MAX_CHALLENGES) {
    // A fail situation
    stats.currentStreak = 0
    stats.gamesFailed += 1
  } else {
    stats.winDistribution[count] += 1
    stats.currentStreak += 1

    if (stats.bestStreak < stats.currentStreak) {
      stats.bestStreak = stats.currentStreak
    }
  }

  stats.successRate = getSuccessRate(stats)

  saveStatsToLocalStorage(stats, company)
  rememberStatsOnServer(stats, company)
  return stats
}

/**
 * Ask the server to keep a copy in a cookie IT sets.
 *
 * localStorage alone is not enough: Safari deletes script-written storage after 7 days
 * without a first-party visit, and a reader who only meets the game embedded in a
 * newsroom's page never visits us first-party - so on Apple devices (about a third of
 * embedded readers) the streak reset roughly every week. A cookie set by the server in
 * an HTTP response is exempt from that sweep. Same technique as News Quiz and News Mix.
 *
 * Fire-and-forget: the reader's result never waits on this, and a failure just means we
 * fall back to localStorage.
 */
const rememberStatsOnServer = (stats: GameStats, company?: string) => {
  if (!company) {
    return
  }
  try {
    const body = new URLSearchParams({
      url: company,
      totalGames: String(stats.totalGames),
      gamesFailed: String(stats.gamesFailed),
      currentStreak: String(stats.currentStreak),
      bestStreak: String(stats.bestStreak),
    })
    // Same origin as the game document for 14 of 15 customers, so the cookie is sent
    // back without any CORS change. credentials:'include' covers the rest.
    fetch('https://newsword.ai/member-area/api/remember-stats', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      keepalive: true,
    }).catch(() => {})
  } catch (e) {
    // Never let remembering a streak cost a reader their game.
  }
}

const defaultStats: GameStats = {
  winDistribution: Array.from(new Array(MAX_CHALLENGES), () => 0),
  gamesFailed: 0,
  currentStreak: 0,
  bestStreak: 0,
  totalGames: 0,
  successRate: 0,
}

export const loadStats = (company?: string) => {
  return loadStatsFromLocalStorage(company) || defaultStats
}

/**
 * Merge whatever the server remembered for this reader with what the browser has.
 *
 * Whichever record shows more completed games is the one that has not been wiped, so
 * it wins. On Apple devices the server copy is often the only survivor; everywhere
 * else the browser copy is usually ahead. Returns null when there is nothing to change,
 * so a caller can skip a pointless re-render.
 */
export const mergeRememberedStats = (
  local: GameStats,
  remembered: any,
  company?: string
): GameStats | null => {
  try {
    if (!remembered) {
      return null
    }
    const total = Number(remembered.totalGames) || 0
    if (total <= (local?.totalGames || 0)) {
      return null
    }
    const merged: GameStats = {
      ...local,
      totalGames: total,
      gamesFailed: Number(remembered.gamesFailed) || 0,
      currentStreak: Number(remembered.currentStreak) || 0,
      bestStreak: Number(remembered.bestStreak) || 0,
      successRate: 0,
    }
    merged.successRate = getSuccessRate(merged)
    saveStatsToLocalStorage(merged, company)
    return merged
  } catch (e) {
    return null
  }
}

const getSuccessRate = (gameStats: GameStats) => {
  const { totalGames, gamesFailed } = gameStats

  return Math.round(
    (100 * (totalGames - gamesFailed)) / Math.max(totalGames, 1)
  )
}
